import React, { useState, useEffect } from 'react';
import './index.css';
import { colors } from './utils/colors';
import type { Teacher, ActiveTab, TeacherAttendanceRecord } from './utils/types';

import SplashScreen     from './components/SplashScreen';
import LoginScreen      from './components/LoginScreen';
import HomeScreen       from './components/HomeScreen';
import ProfileScreen    from './components/ProfileScreen';
import PWAInstallBanner from './components/PWAInstallBanner';
import TeacherAttendance from './modules/teacher-attendance/TeacherAttendance';
import ClassAttendance   from './modules/class-attendance/ClassAttendance';
import InsightsScreen    from './components/InsightsScreen';
import { logout } from './Api/auth';
import AllTeacherAttendance from './modules/all-teacher-attendance-supervisor/AllTeacherAttendance';
import FineComplaintScreen  from './modules/fine-complaint/FineComplaintScreen';
import ExamMarks            from './modules/exam-marks/ExamMarks';

function App() {
  const [splash,  setSplash]  = useState(true);
  const [teacher, setTeacher] = useState<Teacher | null>(() => {
    try {
      const s = localStorage.getItem('vav_teacher');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [activeTab, setActiveTab] = useState<ActiveTab>('home');

  const [myAttendance, setMyAttendance] = useState<TeacherAttendanceRecord | null>(() => {
    try {
      const s = localStorage.getItem('vav_my_attendance');
      if (!s) return null;
      const r: TeacherAttendanceRecord = JSON.parse(s);
      const today = new Date().toISOString().split('T')[0];
      return r.date === today ? r : null;
    } catch { return null; }
  });

  const handleNavigate = (tab: ActiveTab) => {
    if (tab === 'home') {
      // Replace the current entry so the back button never resurfaces a stale tab
      window.history.replaceState({ tab: 'home' }, '', window.location.pathname);
    } else {
      window.history.pushState({ tab }, '', window.location.pathname);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      const prevTab = (e.state as { tab?: ActiveTab } | null)?.tab;
      setActiveTab(prevTab ?? 'home');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    window.history.replaceState({ tab: 'home' }, '', window.location.pathname);
  }, []);

  const handleLogin = (t: Teacher) => {
    setTeacher(t);
    localStorage.setItem('vav_teacher', JSON.stringify(t));
    handleNavigate('home');
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore server errors on logout */ }
    localStorage.removeItem('vav_teacher');
    localStorage.removeItem('vav_my_attendance');
    setTeacher(null);
    setMyAttendance(null);
    window.history.replaceState({ tab: 'home' }, '', window.location.pathname);
    setActiveTab('home');
  };

  const handleSaveMyAttendance = (rec: TeacherAttendanceRecord) => {
    setMyAttendance(rec);
    localStorage.setItem('vav_my_attendance', JSON.stringify(rec));
  };

  if (splash) return <SplashScreen onDone={() => setSplash(false)} />;
  if (!teacher) return <LoginScreen onLogin={handleLogin} />;

  const renderTab = () => {
    switch (activeTab) {
      case 'my-attendance':
        if (teacher.role === 'class_teacher' || teacher.role === 'subject_teacher') {
          handleNavigate('home');
          return null;
        }
        return (
          <TeacherAttendance
            teacher={teacher}
            record={myAttendance}
            onSave={handleSaveMyAttendance}
            onBack={() => handleNavigate('home')}
          />
        );
      case 'class-attendance':
        return <ClassAttendance teacher={teacher} onBack={() => handleNavigate('home')} />;
      case 'all-teacher-attendance':
        if (teacher.role !== 'supervisor' && teacher.role !== 'admin') {
          handleNavigate('home');
          return null;
        }
        return <AllTeacherAttendance onNavigate={handleNavigate} teacher={teacher} />;
      case 'fine-complaint':
        return <FineComplaintScreen teacher={teacher} onBack={() => handleNavigate('home')} />;
      case 'exam-marks':
        return <ExamMarks teacher={teacher} onBack={() => handleNavigate('home')} onNavigate={handleNavigate} />;
      case 'profile':
        return (
          <ProfileScreen
            teacher={teacher}
            onBack={() => handleNavigate('home')}
            onLogout={handleLogout}
            onNavigate={handleNavigate}   // ← added
          />
        );
      case 'insights':
        return (
          <InsightsScreen
            teacher={teacher}
            onLogout={handleLogout}
            onNavigate={handleNavigate}   // ← added
          />
        );
      case 'home':
      default:
        return (
          <HomeScreen
            teacher={teacher}
            myAttendance={myAttendance}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: colors.bg,
      height: '100dvh',
      overflow: 'hidden',
      position: 'fixed',
      width: '100%',
      top: 0,
      left: 0,
    }}>
      {renderTab()}
      <PWAInstallBanner />
    </div>
  );
}

export default App;